import { useEffect, useMemo, useState } from "react";
import { useData } from "@core/lib/useData";
import { useViewState } from "@core/lib/useViewState";
import LazyMap, { type Basemap, type MapPoint } from "@core/components/LazyMap";
import GroupFilter from "@core/components/GroupFilter";
import TrendChart, { type Series } from "@core/components/TrendChart";
import ShareCard from "@core/components/ShareCard";
import { Footer, Header, Splash, Tabs, type Pane } from "@core/components/AppShell";
import WaterWatchCredit from "@core/components/WaterWatchCredit";
import { formatValue, mean, plural } from "@core/lib/format";
import StationList from "./components/StationList";
import StationDetail from "./components/StationDetail";
import { TROPHIC, TROPHIC_ORDER, TSI_BANDS } from "./lib/trophic";
import type { AnnualRow, Payload } from "./lib/types";

/** The metrics the annual series can be charted on, mirroring the tabs the
 *  council's existing dashboard offers. */
const METRICS = {
  secchi_ft: {
    label: "Secchi depth (ft)", unit: "ft", pick: (r: AnnualRow) => r.sf,
    color: "#0E6CA8", bands: false, digits: 1,
  },
  secchi_m: {
    label: "Secchi depth (m)", unit: "m", pick: (r: AnnualRow) => r.sm,
    color: "#0E6CA8", bands: false, digits: 2,
  },
  tsi_secchi: {
    label: "TSI (Secchi)", unit: "TSI", pick: (r: AnnualRow) => r.ts,
    color: "#0B7A4B", bands: true, digits: 1,
  },
  chla: {
    label: "Chlorophyll-a", unit: "µg/L", pick: (r: AnnualRow) => r.ca,
    color: "#16805C", bands: false, digits: 2,
  },
  tsi_chla: {
    label: "TSI (chl-a)", unit: "TSI", pick: (r: AnnualRow) => r.tc,
    color: "#388173", bands: true, digits: 1,
  },
} as const;

type MetricKey = keyof typeof METRICS;

/**
 * What the app opens on. Named explicitly rather than taken from the first
 * key: the list below is ordered alphabetically for the reader, and that
 * ordering must not decide what gets charted first. Secchi depth in feet is
 * the measurement volunteers actually take and the one recorded for every
 * station-year.
 */
const DEFAULT_METRIC: MetricKey = "secchi_ft";

/** Alphabetical by label, in the dropdown and the chart ribbon alike. */
const METRIC_KEYS = (Object.keys(METRICS) as MetricKey[]).sort((a, b) =>
  METRICS[a].label.localeCompare(METRICS[b].label, undefined, { sensitivity: "base" }),
);

export default function App() {
  const { data, error, loading, refreshing, lastRefresh, refresh } = useData<Payload>();
  const view = useViewState("lake");
  const [basemap, setBasemap] = useState<Basemap>("satellite");
  const [pane, setPane] = useState<Pane>("map");

  const metric: MetricKey = (() => {
    const m = view.params.get("metric");
    return m && (METRIC_KEYS as string[]).includes(m) ? (m as MetricKey) : DEFAULT_METRIC;
  })();

  // Selecting a station on a narrow screen should show the map, not leave the
  // user staring at the list they just tapped.
  useEffect(() => {
    if (view.stationId && window.innerWidth <= 1060) setPane("map");
  }, [view.stationId]);

  const selected = useMemo(
    () => data?.stations.find((s) => s.key === view.stationId) ?? null,
    [data, view.stationId],
  );

  // A station's own lake scopes the view even when no lake filter is set, so
  // the list and chart stay in the station's context.
  const activeLake = view.group ?? null;

  const inScope = useMemo(() => {
    if (!data) return [];
    return activeLake ? data.stations.filter((s) => s.lake === activeLake) : data.stations;
  }, [data, activeLake]);

  const rowsInScope = useMemo(() => {
    if (!data) return [];
    if (selected) return data.annual.filter((r) => r.s === selected.key);
    if (activeLake) {
      const ids = new Set(inScope.map((s) => s.key));
      return data.annual.filter((r) => ids.has(r.s));
    }
    return data.annual;
  }, [data, selected, activeLake, inScope]);

  const points: MapPoint[] = useMemo(() => {
    if (!data) return [];
    return data.stations.map((s) => ({
      id: s.key,
      lat: s.lat,
      lng: s.lng,
      color: TROPHIC[s.trophic].color,
      label: `${s.siteId} — ${s.name} (${s.lake})`,
      muted: activeLake ? s.lake !== activeLake : false,
    }));
  }, [data, activeLake]);

  const fitTo = useMemo(
    () => points.filter((p) => !p.muted),
    [points],
  );

  if (loading) return <Splash>Loading lake monitoring data…</Splash>;
  if (error || !data) {
    return (
      <Splash tone="error">
        <strong>Could not load lake monitoring data.</strong>
        <span>{error}</span>
        <button className="btn" onClick={refresh}>Try again</button>
      </Splash>
    );
  }

  const legend = TROPHIC_ORDER
    .map((t) => ({
      key: t,
      label: TROPHIC[t].short,
      color: TROPHIC[t].color,
      n: inScope.filter((s) => s.trophic === t).length,
    }))
    .filter((l) => l.n > 0);

  const M = METRICS[metric];

  // One line per station when a lake is selected, so sites on the same lake
  // can be compared; a single aggregate line otherwise, because 62 overlaid
  // series is noise rather than information.
  const series: Series[] = (() => {
    if (selected) {
      return [{
        key: selected.key,
        label: selected.siteId,
        color: M.color,
        kind: "line" as const,
        points: rowsInScope
          .map((r) => ({ x: r.y, y: M.pick(r) }))
          .filter((p): p is { x: number; y: number } => p.y !== null),
      }];
    }
    if (activeLake && inScope.length > 1 && inScope.length <= 8) {
      const palette = ["#0E6CA8", "#0B7A4B", "#C87E12", "#008DAF", "#915530", "#388173", "#A4161A", "#485070"];
      return inScope.map((s, i) => ({
        key: s.key,
        label: s.siteId,
        color: palette[i % palette.length],
        kind: "line" as const,
        points: rowsInScope
          .filter((r) => r.s === s.key)
          .map((r) => ({ x: r.y, y: M.pick(r), note: s.name }))
          .filter((p): p is { x: number; y: number; note: string } => p.y !== null),
      })).filter((s) => s.points.length > 0);
    }
    // Aggregate: the mean across every station in scope, per year.
    const byYear = new Map<number, number[]>();
    for (const r of rowsInScope) {
      const v = M.pick(r);
      if (v === null) continue;
      const list = byYear.get(r.y) ?? [];
      list.push(v);
      byYear.set(r.y, list);
    }
    return [{
      key: "mean",
      label: activeLake ?? "All lakes",
      color: M.color,
      kind: "line" as const,
      points: [...byYear.entries()]
        .map(([y, vs]) => ({
          x: y,
          y: mean(vs) as number,
          note: `mean of ${plural(vs.length, "station")}`,
        }))
        .sort((a, b) => a.x - b.x),
    }];
  })();

  const chartTitle = selected
    ? `${selected.siteId} — ${M.label}`
    : activeLake
      ? `${activeLake} — ${M.label}`
      : `All lakes — average ${M.label.toLowerCase()} by year`;

  const pointCount = series.reduce((a, s) => a + s.points.length, 0);

  return (
    <div className="app">
      <Header
        program="Volunteer Lake Monitoring"
        programShort="Lake Monitoring"
        legend={legend}
        basemap={basemap}
        onBasemap={setBasemap}
        refreshing={refreshing}
        lastRefresh={lastRefresh}
        onRefresh={refresh}
      />

      <div className="subbar">
        <GroupFilter
          noun="Lake"
          nounPlural="Lakes"
          options={data.lakes}
          value={activeLake}
          onChange={view.selectGroup}
          link={activeLake ? view.groupLink(activeLake) : null}
        />
        <label className="wsfilter">
          <span>Parameter</span>
          <select
            value={metric}
            onChange={(e) =>
              view.setParam("metric", e.target.value === DEFAULT_METRIC ? null : e.target.value)
            }
          >
            {METRIC_KEYS.map((k) => (
              <option key={k} value={k}>{METRICS[k].label}</option>
            ))}
          </select>
        </label>
        {selected && (
          <button className="btn ghost sm" onClick={() => view.selectStation(null)}>
            Clear station
          </button>
        )}
        <span className="subbar-spacer" />
        <span className="subbar-note">
          {activeLake
            ? `${plural(inScope.length, "station")} on ${activeLake}`
            : `${plural(data.stations.length, "station")} · ${data.lakes.length} lakes`}
        </span>
      </div>

      <Tabs pane={pane} onPane={setPane} trendLabel="Trends" />

      <main className={"body" + (selected || activeLake ? " has-detail" : "")}>
        <section className={"pane pane-list" + (pane === "list" ? " show" : "")}>
          <StationList
            stations={inScope}
            selectedId={view.stationId}
            onSelect={view.selectStation}
            lake={activeLake}
          />
        </section>

        <section className={"pane pane-map" + (pane === "map" ? " show" : "")}>
          <LazyMap
            points={points}
            selectedId={view.stationId}
            fitTo={fitTo}
            basemap={basemap}
            token={data.mapboxToken}
            onSelect={view.selectStation}
          />
        </section>

        <section className={"pane pane-chart" + (pane === "trend" ? " show" : "")}>
          <div className="chart-head">
            <h2>{chartTitle}</h2>
            {!selected && <span className="chart-hint">Select a station to filter</span>}
          </div>

          <div className="metric-tabs" role="tablist" aria-label="Metric">
            {METRIC_KEYS.map((k) => (
              <button
                key={k}
                role="tab"
                aria-selected={metric === k}
                className={metric === k ? "on" : ""}
                onClick={() => view.setParam("metric", k === DEFAULT_METRIC ? null : k)}
              >
                {METRICS[k].label}
              </button>
            ))}
          </div>

          <TrendChart
            series={series}
            bands={M.bands ? TSI_BANDS : []}
            yLabel={M.unit}
            format={(v) => formatValue(v, M.digits)}
            emptyMessage="No readings recorded here yet"
          />

          <div className="chart-foot">
            {series.length > 1
              ? series.slice(0, 8).map((s) => (
                <span className="chart-key" key={s.key}>
                  <i style={{ background: s.color }} /> {s.label}
                </span>
              ))
              : <span className="chart-key"><i style={{ background: M.color }} /> {series[0]?.label}</span>}
            {metric.startsWith("tsi_chla") && data.tsiDivergence !== null && (
              <span className="chart-key" style={{ color: "var(--brand-rust)" }}>
                Reads ~{formatValue(data.tsiDivergence, 0)} points below TSI (Secchi) — see notes
              </span>
            )}
            <span className="count">{plural(pointCount, "point")}</span>
          </div>
        </section>

        {selected && (
          <section className={"pane pane-detail show" + (pane === "map" ? " show-narrow" : "")}>
            <StationDetail
              station={selected}
              rows={rowsInScope}
              url={view.permalink}
              lakeLink={view.groupLink(selected.lake)}
              onClose={() => view.selectStation(null)}
              onLake={view.selectGroup}
            />
          </section>
        )}

        {/* With a lake selected but no station, the lake's own permanent link
            is the thing most worth handing to a lake association. On desktop
            it fills the detail column; on narrow screens it would cover the
            map as a sheet, so CSS keeps it to the list tab there. */}
        {!selected && activeLake && (
          <section
            className={"pane pane-detail is-group show" + (pane === "map" ? " show-narrow" : "")}
          >
            <div className="detail">
              <h2 className="detail-name">{activeLake}</h2>
              <p className="detail-sub">
                {plural(inScope.length, "monitoring station")}
                {(() => {
                  const ys = inScope.flatMap((s) => [s.firstYear, s.lastYear])
                    .filter((y): y is number => y !== null);
                  return ys.length ? ` · ${Math.min(...ys)}–${Math.max(...ys)}` : "";
                })()}
              </p>
              <dl className="stats">
                <div>
                  <dt>Stations</dt>
                  <dd>{inScope.length}</dd>
                </div>
                <div>
                  <dt>Mean Secchi</dt>
                  <dd>{formatValue(mean(inScope.map((s) => s.latest.secchiFt)), 1)}</dd>
                </div>
                <div>
                  <dt>Station-years</dt>
                  <dd>{rowsInScope.length}</dd>
                </div>
              </dl>
              <ShareCard
                url={view.groupLink(activeLake)}
                filename={activeLake.replace(/\W+/g, "-").toLowerCase()}
                heading={`Link to ${activeLake}`}
                blurb="Opens this dashboard filtered to this lake — for a lake association page, a newsletter or a sign."
              />
            </div>
          </section>
        )}
      </main>

      <Footer
        items={[
          {
            label: "Updated",
            value: new Date(data.generatedAt).toLocaleString(undefined, {
              month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
            }),
          },
          { label: "Stations", value: String(data.stations.length) },
          { label: "Lakes", value: String(data.lakes.length) },
          {
            label: "Station-years",
            value: data.annual.length.toLocaleString(),
            note: data.rowsExcluded ? `+${data.rowsExcluded} undated` : undefined,
            title: data.rowsExcluded
              ? `${data.rowsExcluded} rows in Airtable have no usable sample year and cannot be placed on a time axis.`
              : undefined,
          },
        ]}
        credit={<WaterWatchCredit />}
        right={
          <a href="https://watershedcouncil.org" target="_blank" rel="noreferrer">
            watershedcouncil.org
          </a>
        }
      />
    </div>
  );
}
